import * as React from 'react';
import { UsersApi } from 'global/api';
import { API, LxConfig, ROUTES } from 'global/constants';
import { history } from 'global/history';
import { LXThunkAction, LXThunkDispatch } from 'global/types';
import { APIPermissions } from 'labxchange-client/models/APIPermissions';
import { checkOnboardingStatus } from 'middleware/onboarder';
import { Action, Dispatch } from 'redux';
import { authLocalStorageManager } from './utils';
import { getSavedLanguage, setLanguageCookie } from 'i18n';
import { showErrorMessage } from 'ui/components/GlobalMessageReporter/dispatch';
import uiMessages from 'ui/components/displayMessages';
import { WrappedMessage } from 'utils';

export enum Types {
    LOGIN_SUCCESS = 'LOGIN_SUCCESS',
    LOGIN_FAILURE = 'LOGIN_FAILURE',
    LOGOUT_SUCCESS = 'LOGOUT_SUCCESS',
}

export interface LoginSuccess extends Action {
    readonly type: Types.LOGIN_SUCCESS;
    readonly token: string;
    readonly name: string;
    readonly username: string;
    readonly avatarUrl: string;
    readonly permissions: APIPermissions;
    readonly onboardingStatus: string;
    readonly acceptedCookieAgreement: boolean | undefined;
}

export interface LoginFailure extends Action {
    readonly type: Types.LOGIN_FAILURE;
    readonly errorMessage: string;
}

export interface LogoutSuccess extends Action {
    readonly type: Types.LOGOUT_SUCCESS;
}

export type ActionTypes =
    | LoginSuccess
    | LoginFailure
    | LogoutSuccess;

/**
 * Asynchronously verify whether or not the recent login attempt succeeded,
 * or whether the login status set by getInitialLoginState() was correct.
 *
 * This method calls an API to confirm whether or not the user is logged in,
 * and gets basic information (username & name) about the logged in user.
 */
export const checkLoginStatus = async (dispatch: LXThunkDispatch) => {
    const token = localStorage.getItem('auth_token');
    if (token === null) {
        // Without an auth token, the user is definitely not logged in.
        markUserAsLoggedOut(dispatch);
        return;
    }
    // Check if the auth token is valid:
    let status;
    try {
        status = await UsersApi.loginStatus();
    } catch (err) {
        if (err.status === 401) {
            // we're not logged in, so do nothing here.
            // The middleware has already dispatched markUserAsLoggedOut.
            return;
        } else {
            // Otherwise, there was some other unexpected non-auth related error,
            // so we should at least warn about it.
            // Do not mark the user as logged out, because the error here is not likely related to auth.
            showErrorMessage(
                <WrappedMessage message={uiMessages.loginStatusError} />,
                {
                    exception: err,
                    confirmText: uiMessages.uiOk,
                },
            );
            return;
        }
    }

    if (status.language && status.language !== getSavedLanguage()) {
        setLanguageCookie(status.language);
        window.location.reload();
        return;
    }

    authLocalStorageManager.name = status.name;
    authLocalStorageManager.username = status.username;
    authLocalStorageManager.onboardingStatus = status.onboardingStatus;
    authLocalStorageManager.avatarUrl = status.avatarUrl;
    dispatch({
        name: status.name,
        token,
        type: Types.LOGIN_SUCCESS,
        username: status.username,
        avatarUrl: status.avatarUrl,
        permissions: status.permissions,
        onboardingStatus: status.onboardingStatus,
        acceptedCookieAgreement: status.acceptedCookieAgreement,
    });

    checkOnboardingStatus();
};

/** Programatic way to redirect to login. You can also use <RedirectToLogin/> */
export const redirectToLogin = (): void => {
    if (window.location.pathname !== ROUTES.General.SIGN_IN) {
        authLocalStorageManager.authRedirectToLocation = window.location;
        // To avoid an infinite series of redirects in the event of an auth_token
        // that is present but expired, clear it just in case:
        localStorage.removeItem('auth_token');
        // Now redirect to the login page:
        history.push(ROUTES.General.SIGN_IN);
    }
};

export const login = (): LXThunkAction<void> => {
    const authUrl = new URL(API.Auth.OAUTH2_AUTHORIZE);
    authUrl.searchParams.set('client_id', LxConfig.Oauth2ClientId);
    authUrl.searchParams.set('response_type', 'token');

    return async (dispatch, getState) => {
        window.location.href = authUrl.toString();
    };
};

export const loginFailure = (message: string) => (dispatch: Dispatch) => {
    dispatch({type: Types.LOGIN_FAILURE, errorMessage: message});
    localStorage.removeItem('auth_token');
};

/**
 * Internal helper to mark the user as logged out.
 * This should not be used directly, but only via
 * logout() or checkLoginStatus()
 */
export const markUserAsLoggedOut = (dispatch: LXThunkDispatch) => {
    dispatch({type: Types.LOGOUT_SUCCESS});
    clearAuthState();
};

const clearAuthState = () => {
    authLocalStorageManager.authToken = null;
    authLocalStorageManager.name = null;
    authLocalStorageManager.username = null;
    authLocalStorageManager.onboardingStatus = null;
    authLocalStorageManager.userBirthdate = null;
};

export const logout = (redirectTo = API.Auth.LOGOUT) => {
    clearAuthState();
    window.location.href = redirectTo;
};
